import React, { useEffect, useMemo, useState } from 'react';
import { isEnabled } from '../../lib/flags';
import './ConsentBanner.css';

const STORAGE_KEY = 'tn-consent-v1';
const ACCEPT = 'accepted';
const DECLINE = 'declined';
const GRANULAR_PREFIX = 'tn-consent-cats-v1';

// Default category state shown in the granular panel. Per DPDP/GDPR,
// only "necessary" cookies are allowed before explicit consent.
const DEFAULT_CATEGORIES = {
  necessary: { granted: true, locked: true,  label: 'Necessary',  description: 'Required for the site to function. Cannot be disabled.' },
  analytics: { granted: false, locked: false, label: 'Analytics',  description: 'Helps us understand which pages perform well. Aggregated, no identifiers.' },
  marketing: { granted: false, locked: false, label: 'Marketing',  description: 'Lets us measure ad performance and remarket to past visitors.' },
};

const readBlanket = () => {
  if (typeof window === 'undefined') return null;
  try { return window.localStorage.getItem(STORAGE_KEY); } catch (_e) { return null; }
};

const readGranular = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(GRANULAR_PREFIX);
    return raw ? JSON.parse(raw) : null;
  } catch (_e) {
    return null;
  }
};

const persistBlanket = (value) => {
  try { window.localStorage.setItem(STORAGE_KEY, value); }
  catch (_e) { window.__tnConsent = value; }
};

const persistGranular = (categories) => {
  try {
    window.localStorage.setItem(GRANULAR_PREFIX, JSON.stringify(categories));
  } catch (_e) { /* private mode — best effort */ }
};

// Public accessor preserved for callers (analytics gating, audit tooling).
export const getConsent = () => readBlanket();
export const getConsentCategories = () => readGranular() || {
  necessary: true,
  analytics: false,
  marketing: false,
};

const applyConsent = (categories) => {
  if (typeof window.gtag === 'function') {
    const analyticsGranted = categories.analytics ? 'granted' : 'denied';
    const marketingGranted = categories.marketing ? 'granted' : 'denied';
    window.gtag('consent', 'update', {
      ad_storage:           marketingGranted,
      ad_user_data:         marketingGranted,
      ad_personalization:   marketingGranted,
      analytics_storage:    analyticsGranted,
    });
  }
  window.dispatchEvent(new CustomEvent('tn:consent', { detail: categories }));
};

const ConsentBanner = () => {
  const [open, setOpen] = useState(false);
  const [granularMode, setGranularMode] = useState(false);
  const [categories, setCategories] = useState(() =>
    Object.fromEntries(Object.entries(DEFAULT_CATEGORIES).map(([k, v]) => [k, v.granted]))
  );

  // Whether to show the "Customise" button next to Accept/Decline.
  const granularEnabled = useMemo(() => isEnabled('granularConsent'), []);

  useEffect(() => {
    const stored = readBlanket() || window.__tnConsent;
    if (!stored) { setOpen(true); return; }
    const granular = readGranular();
    if (granular) {
      setCategories({ ...categories, ...granular });
      applyConsent(granular);
    } else {
      const blanketGrant = stored === ACCEPT;
      const next = { necessary: true, analytics: blanketGrant, marketing: blanketGrant };
      applyConsent(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acceptAll = () => {
    const next = { necessary: true, analytics: true, marketing: true };
    persistBlanket(ACCEPT);
    persistGranular(next);
    applyConsent(next);
    setOpen(false);
  };

  const declineAll = () => {
    const next = { necessary: true, analytics: false, marketing: false };
    persistBlanket(DECLINE);
    persistGranular(next);
    applyConsent(next);
    setOpen(false);
  };

  const savePreferences = () => {
    const next = { ...categories, necessary: true };
    // Treat any non-essential opt-in as "accepted" for the blanket flag,
    // so downstream tools that only check the v1 key see something useful.
    persistBlanket(next.analytics || next.marketing ? ACCEPT : DECLINE);
    persistGranular(next);
    applyConsent(next);
    setOpen(false);
  };

  const toggleCategory = (key) => {
    if (DEFAULT_CATEGORIES[key]?.locked) return;
    setCategories((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!open) return null;

  return (
    <div className="tn-consent" role="dialog" aria-live="polite" aria-label="Cookie consent">
      <div className="tn-consent__inner">
        {!granularMode ? (
          <>
            <p className="tn-consent__copy">
              We use cookies to analyse site usage and improve your experience. You can
              accept analytics cookies, or decline and continue with essential cookies
              only. Read our{' '}
              <a href="/privacy-policy" className="tn-consent__link">privacy policy</a>.
            </p>
            <div className="tn-consent__actions">
              {granularEnabled && (
                <button
                  type="button"
                  className="tn-consent__btn tn-consent__btn--link"
                  onClick={() => setGranularMode(true)}
                >
                  Customise
                </button>
              )}
              <button
                type="button"
                className="tn-consent__btn tn-consent__btn--ghost"
                onClick={declineAll}
              >
                Decline
              </button>
              <button
                type="button"
                className="tn-consent__btn tn-consent__btn--primary"
                onClick={acceptAll}
              >
                Accept analytics
              </button>
            </div>
          </>
        ) : (
          <div className="tn-consent__granular">
            <h2 className="tn-consent__heading">Your cookie preferences</h2>
            {Object.entries(DEFAULT_CATEGORIES).map(([key, meta]) => (
              <label key={key} className={`tn-consent__cat ${meta.locked ? 'tn-consent__cat--locked' : ''}`}>
                <input
                  type="checkbox"
                  checked={!!categories[key]}
                  disabled={meta.locked}
                  onChange={() => toggleCategory(key)}
                />
                <span>
                  <strong>{meta.label}</strong>
                  <span className="tn-consent__cat-desc">{meta.description}</span>
                </span>
              </label>
            ))}
            <div className="tn-consent__actions">
              <button
                type="button"
                className="tn-consent__btn tn-consent__btn--link"
                onClick={() => setGranularMode(false)}
              >
                Back
              </button>
              <button
                type="button"
                className="tn-consent__btn tn-consent__btn--primary"
                onClick={savePreferences}
              >
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsentBanner;
